import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getStudentAttendance } from '../services/attendanceService';
import { getStudentByUserId } from '../services/studentService';
import { AttendanceRecord, Student } from '../types';

const MyAttendance: React.FC = () => {
  const { user } = useAuth();
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [student, setStudent] = useState<Student | null>(null);

  useEffect(() => {
    const fetchStudentAndAttendance = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch the student record for this user by user_id
        const s = await getStudentByUserId(user.id);
        setStudent(s);
        const data = await getStudentAttendance(s.id);
        setAttendance(data);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e.message || 'Failed to fetch attendance');
      } finally {
        setLoading(false);
      }
    };
    fetchStudentAndAttendance();
  }, [user]);

  const presentCount = attendance.filter(a => a.status === 'present').length;
  const absentCount = attendance.filter(a => a.status === 'absent').length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>My Attendance</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        {loading ? <CircularProgress /> : error ? (
          <Alert severity="error">{error}</Alert>
        ) : attendance.length === 0 ? (
          <Typography color="text.secondary">No attendance records found.</Typography>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Present: {presentCount} | Absent: {absentCount}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Review Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendance.slice(0, 30).map((rec, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{rec.date || '-'}</TableCell>
                      <TableCell>{rec.status}</TableCell>
                      <TableCell>{rec.confidence !== undefined ? `${(rec.confidence * 100).toFixed(1)}%` : '-'}</TableCell>
                      <TableCell>{rec.review_status || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MyAttendance; 